/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

/**
 * Dexie Migration Manager
 * Handles post-import data migrations for web platform (IndexedDB/Dexie)
 * 
 * TWO MIGRATION SYSTEMS:
 * 
 * 1. Normal Migrations (dexie.adapter.js):
 *    - Run when user opens app with old database
 *    - Dexie detects version mismatch and runs .upgrade() callbacks
 *    - Example: User has v10 DB, opens v11 app → migration runs automatically
 * 
 * 2. Post-Import Migrations (this file):
 *    - Run when restoring backup from older version
 *    - Database is wiped first, so Dexie can't detect old version
 *    - We manually track backup version and run transformations
 *    - Example: User imports v10 backup into v11 app → this runs v10→v11 migration
 * 
 * CRITICAL: Both systems must have identical transformation logic!
 * 
 * @see docs/MIGRATIONS.md for detailed guide
 */
export class DexieMigrationManager {
  constructor(db) {
    this.db = db
    this.CURRENT_VERSION = 11 // Updated Jan 2026: eartag is now optional
    
    // Migration definitions
    // Index corresponds to target version number
    this.migrations = [
      null, // index 0 (no migration)
      null, // index 1-7 (pre-baseline)
      null, // index 2
      null, // index 3
      null, // index 4
      null, // index 5
      null, // index 6
      null, // index 7
      null, // index 8 (baseline - no migration needed)
      
      // =================================================================
      // FUTURE MIGRATIONS - Add new versions below
      // =================================================================
      
      // VERSION 9 - Notes Array → String (January 2026)
      {
        version: 9,
        description: 'Convert goat notes from array to string',
        migrate: async (db) => {
          console.log('[Post-Import v9] Converting notes array to string...')
          
          const goats = await db.goats.toArray()
          const updates = []
          
          for (const goat of goats) {
            if (Array.isArray(goat.notes)) {
              // Convert array of note objects to newline-separated string
              const newNotes = goat.notes
                .map(note => note?.text || '')
                .filter(text => text.trim())
                .join('\n') || null
              
              goat.notes = newNotes
              updates.push(goat)
            }
          }
          
          if (updates.length > 0) {
            await db.goats.bulkPut(updates)
            console.log(`[Post-Import v9] Converted ${updates.length} goats' notes`)
          } else {
            console.log('[Post-Import v9] No goats with array notes found')
          }
          
          console.log('[Post-Import v9] Migration complete')
        }
      },
      
      // VERSION 10 - Added health_records table (January 2026)
      // Schema-only change - no data transformation needed
      {
        version: 10,
        description: 'Add health_records table',
        migrate: async (db) => {
          console.log('[Post-Import v10] Adding health_records table...')
          // Table is automatically created by Dexie schema definition
          // No data transformation needed
          console.log('[Post-Import v10] Migration complete')
        }
      },
      
      // VERSION 11 - Eartag is now optional (January 2026)
      // Schema-only change - no data transformation needed
      {
        version: 11,
        description: 'Make eartag optional (remove unique constraint)',
        migrate: async (db) => {
          console.log('[Post-Import v11] Making eartag optional...')
          // Index change only - no data transformation needed
          // Dexie schema definition handles the constraint removal
          console.log('[Post-Import v11] Migration complete')
        }
      },
    ]
  }

  /**
   * Run migrations after backup import
   * @param {number} fromVersion - Backup version
   * @param {number} toVersion - Current app version
   * @returns {Promise<void>}
   */
  async runPostImportMigrations(fromVersion, toVersion) {
    if (fromVersion >= toVersion) {
      console.log(`No post-import migrations needed (backup v${fromVersion} → app v${toVersion})`)
      return
    }

    console.log(`Running post-import migrations: v${fromVersion} → v${toVersion}`)

    // Run each migration sequentially
    for (let v = fromVersion + 1; v <= toVersion; v++) {
      await this.runSingleMigration(v)
    }

    console.log('All post-import migrations completed successfully')
  }

  /**
   * Run a single migration
   * @private
   * @param {number} version - Target version to migrate to
   * @returns {Promise<void>}
   */
  async runSingleMigration(version) {
    const migration = this.migrations[version]

    if (!migration) {
      // No migration defined for this version (schema-only change)
      console.log(`[Migration v${version}] No data transformation needed`)
      return
    }

    console.log(`[Migration v${version}] ${migration.description}`)

    try {
      // Run migration within transaction
      await this.db.transaction(
        'rw',
        this.db.groups,
        this.db.goats,
        this.db.weights,
        this.db.temperatures,
        this.db.famacha,
        this.db.dag,
        this.db.health_records,
        async () => {
          await migration.migrate(this.db)
        }
      )

      console.log(`[Migration v${version}] Complete`)
    } catch (error) {
      console.error(`[Migration v${version}] Failed:`, error)
      throw new Error(
        `Post-import migration to v${version} failed: ${error.message}. ` +
        'Data may be in an inconsistent state.'
      )
    }
  }

  /**
   * Get current expected version
   * @returns {number}
   */
  getCurrentVersion() {
    return this.CURRENT_VERSION
  }

  /**
   * Check if migrations exist between two versions
   * @param {number} fromVersion
   * @param {number} toVersion
   * @returns {boolean}
   */
  hasMigrations(fromVersion, toVersion) {
    for (let v = fromVersion + 1; v <= toVersion; v++) {
      if (this.migrations[v]) {
        return true
      }
    }
    return false
  }

  /**
   * Get list of migrations that will run
   * @param {number} fromVersion
   * @param {number} toVersion
   * @returns {Array<{version: number, description: string}>}
   */
  getPendingMigrations(fromVersion, toVersion) {
    const pending = []
    
    for (let v = fromVersion + 1; v <= toVersion; v++) {
      if (this.migrations[v]) {
        pending.push({
          version: v,
          description: this.migrations[v].description
        })
      }
    }
    
    return pending
  }
}
